import { hostHasWildcardOnlyAtLabelStartOrMiddle } from '@terra/shared-asset-processing';

const DOMAIN_WILDCARD_PATTERN = /^(?:[a-zA-Z0-9*-]{1,63}\.)+[a-zA-Z]{2,63}$/;
const DOMAIN_PATTERN = /^(?!:\/\/)(?:[a-zA-Z0-9-]{1,63}\.)+[a-zA-Z]{2,63}$/;
const IPV4_OCTET = '(?:25[0-5]|2[0-4]\\d|1\\d{2}|[1-9]?\\d)';
const IPV4_HOST_PATTERN = new RegExp(`^${IPV4_OCTET}(?:\\.${IPV4_OCTET}){3}$`);
const DOMAIN_PORT_PATTERN = /^[1-9]\d{0,4}$/;
const MAX_DOMAIN_PORT = 65535;

const domainHostIpCandidate = (host: string): string => {
  if (host.startsWith('[') && host.endsWith(']')) {
    return host.slice(1, -1);
  }

  return host;
};

const isValidIpv4Host = (host: string): boolean => IPV4_HOST_PATTERN.test(host);

const isValidIpv6Host = (host: string): boolean => {
  try {
    const parsedHostname = domainHostIpCandidate(
      new URL(`http://[${host}]/`).hostname,
    );

    return parsedHostname === host;
  } catch {
    return false;
  }
};

const isValidIpHost = (host: string): boolean => {
  const candidate = domainHostIpCandidate(host);

  return isValidIpv4Host(candidate) || isValidIpv6Host(candidate);
};

const isValidHostname = (host: string): boolean => {
  if (!host || host.includes('/')) {
    return false;
  }

  if (host === 'localhost') {
    return true;
  }

  if (isValidIpHost(host)) {
    return true;
  }

  const matchesPattern =
    DOMAIN_WILDCARD_PATTERN.test(host) || DOMAIN_PATTERN.test(host);

  if (!matchesPattern) {
    return false;
  }

  return hostHasWildcardOnlyAtLabelStartOrMiddle(host);
};

const parsePort = (value: string): number | null => {
  if (!DOMAIN_PORT_PATTERN.test(value)) {
    return null;
  }

  const port = Number(value);

  if (port > MAX_DOMAIN_PORT) {
    return null;
  }

  return port;
};

const splitUserinfo = (
  value: string,
): { userinfo: string | null; hostport: string } | null => {
  const atIndex = value.lastIndexOf('@');

  if (atIndex === -1) {
    return { userinfo: null, hostport: value };
  }

  const userinfo = value.slice(0, atIndex);
  const hostport = value.slice(atIndex + 1);

  if (!userinfo || !hostport) {
    return null;
  }

  return { userinfo, hostport };
};

const splitHostport = (
  hostport: string,
): { hostname: string; port: number | null } | null => {
  if (hostport.startsWith('[')) {
    const closeBracket = hostport.indexOf(']');

    if (closeBracket <= 1) {
      return null;
    }

    const hostname = hostport.slice(1, closeBracket);
    const rest = hostport.slice(closeBracket + 1);

    if (!rest) {
      return { hostname, port: null };
    }

    if (!rest.startsWith(':')) {
      return null;
    }

    const port = parsePort(rest.slice(1));

    if (port === null) {
      return null;
    }

    return { hostname, port };
  }

  const firstColon = hostport.indexOf(':');
  const lastColon = hostport.lastIndexOf(':');

  if (firstColon !== -1 && firstColon === lastColon) {
    const hostname = hostport.slice(0, firstColon);
    const port = parsePort(hostport.slice(firstColon + 1));

    if (!hostname || port === null) {
      return null;
    }

    return { hostname, port };
  }

  return { hostname: hostport, port: null };
};

export type DomainAuthorityParts = {
  userinfo: string | null;
  hostname: string;
  port: number | null;
};

export class DomainAuthority {
  readonly userinfo: string | null;
  readonly hostname: string;
  readonly port: number | null;

  private constructor({ userinfo, hostname, port }: DomainAuthorityParts) {
    this.userinfo = userinfo;
    this.hostname = hostname;
    this.port = port;
  }

  static parse(value: string): DomainAuthority | null {
    const trimmed = value.trim();

    if (!trimmed || trimmed.includes('/') || /\s/.test(trimmed)) {
      return null;
    }

    const userinfoAndHost = splitUserinfo(trimmed);

    if (!userinfoAndHost) {
      return null;
    }

    const hostport = splitHostport(userinfoAndHost.hostport);

    if (!hostport || !isValidHostname(hostport.hostname)) {
      return null;
    }

    return new DomainAuthority({
      userinfo: userinfoAndHost.userinfo,
      hostname: hostport.hostname,
      port: hostport.port,
    });
  }

  static isValid(value: string): boolean {
    return DomainAuthority.parse(value) !== null;
  }
}
